import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft, FaDownload } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { downloadCsv } from "../utils/csv";
import {
  formatDateTime,
  maskPayoutValue,
  useWalletAudit,
  useWalletTransactions,
  useWalletUser,
} from "../hooks/useWalletAdminData";
import {
  adminWalletAddNote,
  saveWalletPayoutAccounts,
} from "../services/walletAdminService";
import { canMutateWallet, canViewWalletModule } from "../utils/walletPermissions";
import "./walletAdmin.css";

function money(v) {
  return Number(v || 0).toFixed(2);
}

function normalizeAccountForSave(account = {}, idx = 0, primaryId = "") {
  const id = String(account.id || account.accountId || `acc_${idx + 1}`);
  return {
    id,
    method: String(account.method || ""),
    holderName: String(account.holderName || account.accountHolder || ""),
    accountNumber: String(account.accountNumber || ""),
    ifsc: String(account.ifsc || ""),
    upiId: String(account.upiId || ""),
    accountLabel: String(account.accountLabel || ""),
    nameVerified: Boolean(account.nameVerified),
    isPrimary: String(primaryId || "") === id,
    isActive: account.isActive === false ? false : true,
    adminNote: String(account.adminNote || ""),
    updatedAt: new Date().toISOString(),
  };
}

export default function WalletDetail() {
  const { phone = "" } = useParams();
  const navigate = useNavigate();
  const canView = canViewWalletModule();
  const canMutate = canMutateWallet();

  const { user, loading, error } = useWalletUser(phone);
  const [direction, setDirection] = useState("");
  const [sourceType, setSourceType] = useState("");
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const txState = useWalletTransactions(phone, {
    maxItems: 600,
    direction,
    sourceType,
    status,
    dateFrom,
    dateTo,
  });

  const auditState = useWalletAudit({ phone, maxItems: 400 });

  const [accounts, setAccounts] = useState([]);
  const [primaryAccountId, setPrimaryAccountId] = useState("");
  const [savingAccounts, setSavingAccounts] = useState(false);
  const [adminNote, setAdminNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  useEffect(() => {
    if (!user) return;
    const list = Array.isArray(user.walletPayoutAccounts) ? user.walletPayoutAccounts : [];
    setAccounts(list.map((a) => ({ ...a })));
    const currentPrimary = list.find((a) => a.isPrimary)?.id || user.walletPrimaryAccountId || list[0]?.id || "";
    setPrimaryAccountId(String(currentPrimary || ""));
  }, [user]);

  const txCsvRows = useMemo(() => (
    (txState.rows || []).map((row) => ({
      createdAt: formatDateTime(row.createdAt),
      type: row.type || "",
      title: row.title || "",
      sourceType: row.sourceType || "",
      direction: row.direction || "",
      amount: row.amount ?? "",
      status: row.status || "",
      settlementStatus: row.settlementStatus || "",
      requestId: row.requestId || "",
      settlementTxnId: row.settlementTxnId || "",
      utrId: row.utrId || "",
      adminNote: row.adminNote || "",
      transactionId: row.id || "",
    }))
  ), [txState.rows]);

  const saveAccounts = async (noteText = "") => {
    try {
      setSavingAccounts(true);
      const prepared = accounts.map((acc, idx) => normalizeAccountForSave(acc, idx, primaryAccountId));
      await saveWalletPayoutAccounts({
        phone,
        accounts: prepared,
        primaryAccountId,
        adminNote: noteText,
      });
      toast.success("Payout accounts updated");
    } catch (err) {
      toast.error(err?.message || "Failed to update payout accounts");
    } finally {
      setSavingAccounts(false);
    }
  };

  if (!canView) {
    return (
      <div className="wallet-card wallet-page">
        <h3 style={{ marginTop: 0 }}>Access Denied</h3>
        <p>Only admin/super_admin can access wallet ledger.</p>
      </div>
    );
  }

  if (loading) return <div className="wallet-card wallet-page">Loading wallet...</div>;

  if (error || !user) {
    return (
      <div className="wallet-card wallet-page">
        <h3 style={{ marginTop: 0 }}>Wallet not found</h3>
        <p>{error || "No user wallet found for this phone."}</p>
      </div>
    );
  }

  return (
    <div className="wallet-page">
      <div className="wallet-title-row">
        <div>
          <button className="wallet-btn wallet-btn-light" onClick={() => navigate("/panel/wallets")}>
            <FaArrowLeft style={{ marginRight: 6 }} />
            Back to Manage Wallets
          </button>
          <h2 style={{ margin: "10px 0 0" }}>Wallet Ledger: {user.phone}</h2>
          <div style={{ color: "#64748b", marginTop: 4 }}>
            {user.name || "-"} | {user.role || "-"}
          </div>
        </div>
        <span className={`wallet-chip ${user.walletBlocked ? "chip-blocked" : "chip-active"}`}>
          {user.walletBlocked ? "Blocked" : "Active"}
        </span>
      </div>

      <div className="wallet-grid wallet-grid-kpi">
        <div className="wallet-card wallet-kpi-card">
          <div className="wallet-kpi-title">Wallet Balance</div>
          <div className="wallet-kpi-value">Rs {money(user.walletBalance)}</div>
        </div>
        <div className="wallet-card wallet-kpi-card">
          <div className="wallet-kpi-title">On Hold</div>
          <div className="wallet-kpi-value">Rs {money(user.walletOnHold)}</div>
        </div>
        <div className="wallet-card wallet-kpi-card">
          <div className="wallet-kpi-title">Lifetime Earnings</div>
          <div className="wallet-kpi-value">Rs {money(user.walletLifetimeEarnings)}</div>
        </div>
        <div className="wallet-card wallet-kpi-card">
          <div className="wallet-kpi-title">Last Updated</div>
          <div className="wallet-kpi-value wallet-kpi-value-sm">{formatDateTime(user.walletUpdatedAt)}</div>
        </div>
      </div>

      <div className="wallet-grid-2" style={{ marginTop: 12 }}>
        <div className="wallet-card">
          <div className="wallet-title-row" style={{ marginBottom: 8 }}>
            <h4 style={{ margin: 0 }}>Payout Accounts</h4>
            <button
              className="wallet-btn wallet-btn-light"
              disabled={!canMutate || savingAccounts}
              onClick={() => saveAccounts("")}
            >
              {savingAccounts ? "Saving..." : "Save Accounts"}
            </button>
          </div>

          {!accounts.length ? <div className="wallet-empty">No payout account found.</div> : null}
          {accounts.map((acc, idx) => (
            <div key={acc.id || idx} className="wallet-account-card">
              <div className="wallet-account-head">
                <div>
                  <div style={{ fontWeight: 700 }}>
                    {acc.method || "-"} {acc.accountLabel ? `| ${acc.accountLabel}` : ""}
                  </div>
                  <div style={{ color: "#64748b", fontSize: 12 }}>
                    {acc.holderName || "-"} | {maskPayoutValue(acc)}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <span className={`wallet-chip ${acc.nameVerified ? "chip-active" : "chip-onhold"}`}>
                    {acc.nameVerified ? "Name Verified" : "Name Not Verified"}
                  </span>
                  <span className={`wallet-chip ${String(primaryAccountId) === String(acc.id) ? "chip-primary" : "chip-light"}`}>
                    {String(primaryAccountId) === String(acc.id) ? "Primary" : "Secondary"}
                  </span>
                </div>
              </div>
              <div className="wallet-account-controls">
                <label className="wallet-checkline">
                  <input
                    type="checkbox"
                    checked={acc.isActive !== false}
                    disabled={!canMutate}
                    onChange={(e) => {
                      const next = [...accounts];
                      next[idx] = { ...next[idx], isActive: e.target.checked };
                      setAccounts(next);
                    }}
                  />
                  Active
                </label>
                <button
                  className="wallet-btn wallet-btn-light"
                  disabled={!canMutate}
                  onClick={() => setPrimaryAccountId(String(acc.id || ""))}
                >
                  Set Primary
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="wallet-card">
          <h4 style={{ marginTop: 0 }}>Admin Notes & Audit</h4>
          <textarea
            className="wallet-textarea"
            placeholder="Add admin note for this wallet"
            value={adminNote}
            onChange={(e) => setAdminNote(e.target.value)}
          />
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button
              className="wallet-btn wallet-btn-primary"
              disabled={!canMutate || savingNote}
              onClick={async () => {
                const note = adminNote.trim();
                if (!note) {
                  toast.error("Enter note before saving");
                  return;
                }
                try {
                  setSavingNote(true);
                  await adminWalletAddNote({ phone, note });
                  await saveAccounts(note);
                  toast.success("Admin note saved");
                  setAdminNote("");
                } catch (err) {
                  toast.error(err?.message || "Failed to save admin note");
                } finally {
                  setSavingNote(false);
                }
              }}
            >
              {savingNote ? "Saving..." : "Add Note"}
            </button>
          </div>

          <div className="wallet-audit-list">
            {auditState.loading ? <div className="wallet-empty">Loading audit timeline...</div> : null}
            {!auditState.loading && !auditState.rows.length ? (
              <div className="wallet-empty">No audit entries yet.</div>
            ) : null}
            {auditState.rows.map((row) => (
              <div key={row.id} className="wallet-audit-item">
                <div className="wallet-audit-line">
                  <strong>{row.actionType || "-"}</strong>
                  <span>{formatDateTime(row.createdAt)}</span>
                </div>
                <div className="wallet-audit-line">
                  <span>{row.reason || "-"}</span>
                  <span>{row.adminEmail || row.adminUid || "-"}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="wallet-card" style={{ marginTop: 12 }}>
        <div className="wallet-title-row" style={{ marginBottom: 8 }}>
          <h4 style={{ margin: 0 }}>Transaction History</h4>
          <button
            className="wallet-btn wallet-btn-light"
            onClick={() => downloadCsv(`wallet_ledger_${phone}.csv`, txCsvRows)}
          >
            <FaDownload style={{ marginRight: 6 }} />
            Export CSV
          </button>
        </div>

        <div className="wallet-filter-grid">
          <input
            className="wallet-input"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
          <input
            className="wallet-input"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
          <select className="wallet-select" value={direction} onChange={(e) => setDirection(e.target.value)}>
            <option value="">All Directions</option>
            <option value="credit">Credit</option>
            <option value="debit">Debit</option>
          </select>
          <select className="wallet-select" value={sourceType} onChange={(e) => setSourceType(e.target.value)}>
            <option value="">All Sources</option>
            <option value="event_booking">Event Booking</option>
            <option value="mentor_task">Mentor Task</option>
            <option value="song_request">Song Request</option>
            <option value="announcement">Announcement</option>
            <option value="withdrawal">Withdrawal</option>
            <option value="topup">Topup</option>
            <option value="admin_adjustment">Admin Adjustment</option>
          </select>
          <input
            className="wallet-input"
            placeholder="Status (success, processing, credited...)"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          />
        </div>

        {txState.loading ? <div className="wallet-empty">Loading transactions...</div> : null}
        {!txState.loading && txState.error ? <div className="wallet-empty">{txState.error}</div> : null}
        {!txState.loading && !txState.error && !txState.rows.length ? (
          <div className="wallet-empty">No transactions found.</div>
        ) : null}

        {!txState.loading && !txState.error && txState.rows.length ? (
          <div className="wallet-table-wrap">
            <table className="wallet-table wallet-table-wide">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Direction</th>
                  <th>Source</th>
                  <th>Status</th>
                  <th>Settlement</th>
                  <th>Amount</th>
                  <th>Request ID</th>
                  <th>Title / Note</th>
                </tr>
              </thead>
              <tbody>
                {txState.rows.map((row) => (
                  <tr key={row.id}>
                    <td>{formatDateTime(row.createdAt)}</td>
                    <td>{row.type || "-"}</td>
                    <td>{row.direction || "-"}</td>
                    <td>{row.sourceType || "-"}</td>
                    <td>{row.status || "-"}</td>
                    <td>{row.settlementStatus || "-"}</td>
                    <td>Rs {money(row.amount)}</td>
                    <td>{row.requestId || "-"}</td>
                    <td>{row.title || "-"}{row.adminNote ? ` | ${row.adminNote}` : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      <ToastContainer position="top-right" autoClose={2600} />
    </div>
  );
}
